import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { assetToast } from '@xdocs/i18n';
import { useI18n, useToast } from '@xdocs/ui';
import type { ExplorerFileEntry, ExplorerFolderEntry, Folder } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';
import { EXPLORER_LIST_QUERY_KEY_PREFIX } from './useExplorerList.js';
import { useExplorerT } from './useExplorerT.js';

/** Undo window (ux.md "Low-stakes/reversible → no dialog, use an Undo toast,
 * 5-10s window"): the actual `file.delete`/`folder.delete` op is deferred
 * until this elapses with no Undo click — there is no restore/undelete op
 * in the frozen contract, so "undo" here means "never actually called
 * delete yet", not a server-side revert. */
const UNDO_WINDOW_MS = 6000;

export interface UseExplorerMutationsResult {
  /** Ids currently inside their undo window — the caller filters these out
   * of the rendered rows for the optimistic "it's gone" feel. */
  pendingDeleteIds: ReadonlySet<string>;
  requestDeleteFile: (entry: ExplorerFileEntry) => void;
  requestDeleteFolder: (entry: ExplorerFolderEntry) => void;
  renameFile: (fileId: string, name: string) => Promise<void>;
  renameFolder: (folderId: string, name: string) => Promise<void>;
  moveFile: (fileId: string, folderId: string | null) => Promise<void>;
  moveFolder: (folderId: string, parentFolderId: string | null) => Promise<void>;
  createFolder: (name: string, parentFolderId: string | null) => Promise<Folder>;
}

/** Explorer write ops (F7): rename/move/create invalidate the shared
 * `explorer-list` query-key prefix on success (E1's write-site invalidation
 * half — the live `user:{userId}` push in `useExplorerList` is the other
 * half); delete uses the optimistic Undo-toast pattern instead of a
 * confirm dialog for the reversible common case. */
export function useExplorerMutations(): UseExplorerMutationsResult {
  const { wsClient } = useWs();
  const { locale } = useI18n();
  const { t } = useExplorerT();
  const { push: pushToast } = useToast();
  const queryClient = useQueryClient();
  const [pendingDeleteIds, setPendingDeleteIds] = useState<ReadonlySet<string>>(new Set());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const invalidateList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [EXPLORER_LIST_QUERY_KEY_PREFIX] });
  }, [queryClient]);

  function clearPending(id: string): void {
    setPendingDeleteIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }

  const scheduleDelete = useCallback(
    (id: string, name: string, run: () => Promise<unknown>) => {
      setPendingDeleteIds((prev) => new Set(prev).add(id));
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        void run()
          .then(() => invalidateList())
          .catch(() => {
            clearPending(id);
            pushToast(assetToast(locale, 'error', t('explorer:action.delete'), name));
          });
      }, UNDO_WINDOW_MS);
      timersRef.current.set(id, timer);

      pushToast({
        ...assetToast(locale, 'success', t('explorer:toast.deleted.action'), name),
        durationMs: UNDO_WINDOW_MS,
        undoLabel: t('explorer:toast.deleted.undo'),
        onUndo: () => clearPending(id),
      });
    },
    [invalidateList, locale, pushToast, t],
  );

  const requestDeleteFile = useCallback(
    (entry: ExplorerFileEntry) => {
      scheduleDelete(entry.id, entry.name, () => requestTyped(wsClient, 'file.delete', { fileId: entry.id }));
    },
    [scheduleDelete, wsClient],
  );

  const requestDeleteFolder = useCallback(
    (entry: ExplorerFolderEntry) => {
      scheduleDelete(entry.id, entry.name, () => requestTyped(wsClient, 'folder.delete', { folderId: entry.id }));
    },
    [scheduleDelete, wsClient],
  );

  const renameFile = useCallback(
    async (fileId: string, name: string): Promise<void> => {
      await requestTyped(wsClient, 'file.rename', { fileId, name });
      invalidateList();
    },
    [wsClient, invalidateList],
  );

  const renameFolder = useCallback(
    async (folderId: string, name: string): Promise<void> => {
      await requestTyped(wsClient, 'folder.rename', { folderId, name });
      invalidateList();
    },
    [wsClient, invalidateList],
  );

  const moveFile = useCallback(
    async (fileId: string, folderId: string | null): Promise<void> => {
      await requestTyped(wsClient, 'file.move', { fileId, folderId });
      invalidateList();
    },
    [wsClient, invalidateList],
  );

  const moveFolder = useCallback(
    async (folderId: string, parentFolderId: string | null): Promise<void> => {
      await requestTyped(wsClient, 'folder.move', { folderId, parentFolderId });
      invalidateList();
    },
    [wsClient, invalidateList],
  );

  const createFolder = useCallback(
    async (name: string, parentFolderId: string | null): Promise<Folder> => {
      const folder = await requestTyped(wsClient, 'folder.create', { name, parentFolderId });
      invalidateList();
      return folder;
    },
    [wsClient, invalidateList],
  );

  return {
    pendingDeleteIds,
    requestDeleteFile,
    requestDeleteFolder,
    renameFile,
    renameFolder,
    moveFile,
    moveFolder,
    createFolder,
  };
}
